import React, { useEffect } from 'react';
import { CONTACT } from './landingConfig';
import { FloatingWhatsApp, Footer, Header, MobileCta } from './LandingPage';
import usePageMeta from './usePageMeta';
import './Landing.css';

const UPDATED = '30 de septiembre de 2026';

// Nota: textos redactados según el funcionamiento real del sitio y del sistema.
// Recomendado: revisión por un abogado y completar razón social / RUT del responsable.

const Privacy = () => (
  <>
    <h1>Política de privacidad</h1>
    <p className="lp-legal-date">Última actualización: {UPDATED}</p>

    <p>
      En Medix cuidamos la información de las personas que visitan medix.cl, de las clínicas y profesionales que usan
      nuestro sistema y de sus pacientes. Esta política explica qué datos tratamos, para qué, dónde se guardan y cómo
      puedes ejercer tus derechos, conforme a la Ley N° 19.628 sobre protección de la vida privada y la Ley N° 21.719
      sobre protección de datos personales.
    </p>

    <h2>1. Quién es responsable</h2>
    <p>
      <strong>Datos de visitantes y clientes de Medix</strong> (formulario de contacto, cuentas de usuario, facturación):
      Medix es responsable de su tratamiento.
    </p>
    <p>
      <strong>Datos de pacientes</strong> que cada clínica o profesional registra en el sistema: la clínica o profesional
      es el responsable de esos datos, y Medix actúa como encargado del tratamiento, procesándolos solo para prestar el
      servicio y según sus instrucciones.
    </p>
    <p>Contacto para temas de privacidad: <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>.</p>

    <h2>2. Qué datos tratamos</h2>
    <ul>
      <li><strong>Formulario de contacto:</strong> nombre, clínica o consulta, email, teléfono, plan de interés y mensaje.</li>
      <li><strong>Cuentas del sistema:</strong> nombre, email, rol dentro de la clínica y datos de la clínica.</li>
      <li><strong>Datos que registra la clínica:</strong> información de pacientes, agenda, fichas clínicas, consentimientos, recetas, informes y archivos asociados. Pueden incluir datos de salud, que la ley considera sensibles.</li>
      <li><strong>Registros técnicos y de auditoría:</strong> acciones realizadas dentro del sistema (quién hizo qué y cuándo), para seguridad y trazabilidad.</li>
    </ul>

    <h2>3. Para qué los usamos</h2>
    <ul>
      <li>Responder solicitudes de información y coordinar demostraciones o pilotos.</li>
      <li>Prestar el servicio: crear cuentas, permitir el acceso y guardar la información que registra cada clínica.</li>
      <li>Seguridad, prevención de accesos indebidos y cumplimiento legal.</li>
      <li>Facturación y gestión comercial con clientes.</li>
    </ul>
    <p>No vendemos datos personales ni los usamos para publicidad de terceros.</p>

    <h2>4. Dónde se guardan</h2>
    <p>
      Medix funciona sobre la infraestructura de Google Cloud (Firebase). La base de datos está alojada en la región de
      Santiago de Chile. Algunos servicios asociados, como el almacenamiento de archivos o el envío de correos, pueden
      operar en servidores fuera de Chile, con las medidas de seguridad del proveedor.
    </p>

    <h2>5. Quién puede acceder</h2>
    <ul>
      <li>Dentro de cada clínica, solo las personas con el rol y los permisos que la clínica define.</li>
      <li>Medix accede a los datos solo cuando es necesario para dar soporte o mantener el servicio.</li>
      <li>Proveedores tecnológicos que nos permiten operar (por ejemplo, Google Cloud), bajo sus condiciones de confidencialidad y seguridad.</li>
      <li>Autoridades, cuando la ley lo exija.</li>
    </ul>

    <h2>6. Seguridad</h2>
    <p>
      Usamos conexiones cifradas (HTTPS), control de acceso por roles, reglas de seguridad que aíslan la información de
      cada clínica y registro de auditoría de las acciones en el sistema.
    </p>

    <h2>7. Cuánto tiempo los guardamos</h2>
    <p>
      Los datos del formulario de contacto se conservan mientras sean útiles para la relación comercial o hasta que
      pidas su eliminación. Los datos de cada clínica se conservan mientras su cuenta esté activa; al terminar el
      servicio, la clínica puede solicitar su exportación o eliminación, sin perjuicio de los plazos de conservación
      que exija la normativa de salud aplicable.
    </p>

    <h2>8. Tus derechos</h2>
    <p>
      Puedes solicitar acceso, rectificación, supresión, oposición y portabilidad de tus datos, y el bloqueo o
      limitación de su tratamiento, escribiendo a <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>. Si eres
      paciente de una clínica que usa Medix, dirige tu solicitud a esa clínica; nosotros la apoyaremos para responderla.
    </p>

    <h2>9. Cookies y almacenamiento local</h2>
    <p>
      medix.cl no usa cookies de publicidad ni de seguimiento de terceros. El sistema usa el almacenamiento del
      navegador solo para mantener tu sesión iniciada.
    </p>

    <h2>10. Cambios</h2>
    <p>Podemos actualizar esta política. Publicaremos aquí la versión vigente con su fecha de actualización.</p>
  </>
);

const Terms = () => (
  <>
    <h1>Términos y condiciones de uso</h1>
    <p className="lp-legal-date">Última actualización: {UPDATED}</p>

    <p>
      Estos términos regulan el uso de medix.cl y del sistema de gestión clínica Medix (el "Servicio"). Al crear una
      cuenta o usar el Servicio, aceptas estas condiciones.
    </p>

    <h2>1. El Servicio</h2>
    <p>
      Medix es un software en la nube para la gestión de consultas y clínicas: agenda, pacientes, fichas clínicas,
      consentimientos, recetas, informes y módulos por especialidad. Algunas funciones pueden estar en etapa beta o
      marcadas como "próximamente".
    </p>

    <h2>2. Cuentas y uso</h2>
    <ul>
      <li>Eres responsable de mantener la confidencialidad de tus credenciales y de las acciones realizadas con tu cuenta.</li>
      <li>La clínica define los roles y permisos de su equipo.</li>
      <li>No está permitido usar el Servicio para fines ilícitos, intentar acceder a información de otras clínicas o afectar su funcionamiento.</li>
    </ul>

    <h2>3. Datos de pacientes</h2>
    <p>
      La clínica o profesional es responsable de contar con las bases legales y los consentimientos necesarios para
      registrar datos de sus pacientes, y de la exactitud de la información clínica. Medix trata esos datos como
      encargado, según la <a href="/privacidad">Política de privacidad</a>.
    </p>

    <h2>4. Planes, pagos y piloto</h2>
    <ul>
      <li>Los precios publicados están en pesos chilenos y no incluyen IVA.</li>
      <li>El piloto de 30 días es pagado; si continúas con un plan, su valor se descuenta del primer mes.</li>
      <li>El plan mensual no tiene permanencia. El pago anual considera 2 meses de descuento.</li>
      <li>El no pago puede significar la suspensión del acceso, previo aviso.</li>
    </ul>

    <h2>5. Disponibilidad y soporte</h2>
    <p>
      Trabajamos para que el Servicio esté disponible de forma continua, pero pueden existir interrupciones por
      mantención o causas externas. Damos soporte por WhatsApp y email.
    </p>

    <h2>6. Propiedad intelectual</h2>
    <p>
      El software, la marca y los contenidos de Medix pertenecen a Medix. La información que registra cada clínica es
      de la clínica.
    </p>

    <h2>7. Término del servicio</h2>
    <p>
      Puedes terminar el Servicio en cualquier momento en el plan mensual. Al terminar, puedes solicitar la exportación
      de tu información dentro de un plazo razonable, antes de su eliminación.
    </p>

    <h2>8. Responsabilidad</h2>
    <p>
      Medix es una herramienta de apoyo a la gestión y no reemplaza el criterio clínico de los profesionales de la
      salud. En la medida que la ley lo permita, la responsabilidad de Medix se limita al monto pagado por el Servicio
      en los últimos 12 meses.
    </p>

    <h2>9. Ley aplicable</h2>
    <p>Estos términos se rigen por las leyes de la República de Chile.</p>

    <h2>10. Contacto</h2>
    <p>Consultas sobre estos términos: <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>.</p>
  </>
);

const LegalPage = ({ kind }) => {
  const isPrivacy = kind === 'privacidad';
  usePageMeta(isPrivacy ? '/privacidad' : '/terminos');
  useEffect(() => { window.scrollTo(0, 0); }, [kind]);
  return (
    <div className="lp" id="top">
      <Header solid />
      <main className="lp-legal">
        <div className="lp-container lp-legal-inner">{isPrivacy ? <Privacy /> : <Terms />}</div>
      </main>
      <Footer />
      <FloatingWhatsApp />
      <MobileCta />
    </div>
  );
};

export default LegalPage;
